import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChatCircleText, CaretDown, Plus, Star, Trash, Eye, EyeSlash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = ['Novel', 'Screenplay', 'Song', 'Other'];

const StarRow = ({ rating, onChange, readOnly = false, size = 16 }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={readOnly}
        onClick={() => !readOnly && onChange?.(n)}
        className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        data-testid={`star-${n}`}
        aria-label={`${n} star`}
      >
        <Star size={size} weight={n <= rating ? 'fill' : 'regular'} className={n <= rating ? 'text-gold' : 'text-ash/50'} />
      </button>
    ))}
  </div>
);

export const ReviewsAdmin = () => {
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [location, setLocation] = useState('');
  const [projectType, setProjectType] = useState('Novel');
  const [published, setPublished] = useState(true);

  const inputCls = "w-full px-4 py-3 rounded-lg border border-border bg-background text-ivory placeholder:text-ash focus:border-gold focus:ring-1 focus:ring-gold/40 outline-none transition-colors font-sans text-sm";

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
      setReviews(r.data.reviews || []);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Could not load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchReviews();
  }, [open, fetchReviews]);

  const resetForm = () => {
    setName(''); setRating(5); setText(''); setLocation(''); setProjectType('Novel'); setPublished(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      toast.error('Name and review text are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/reviews`, {
        name: name.trim(),
        rating,
        text: text.trim(),
        location: location.trim() || null,
        project_type: projectType || null,
        published,
      }, { withCredentials: true });
      toast.success('Review added');
      resetForm();
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create review');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublished = async (review) => {
    try {
      await axios.patch(`${API}/admin/reviews/${review.review_id}`, { published: !review.published }, { withCredentials: true });
      setReviews((prev) => prev.map((r) => r.review_id === review.review_id ? { ...r, published: !review.published } : r));
    } catch {
      toast.error('Could not update');
    }
  };

  const handleDelete = async (review) => {
    if (!window.confirm(`Delete review by ${review.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/reviews/${review.review_id}`, { withCredentials: true });
      setReviews((prev) => prev.filter((r) => r.review_id !== review.review_id));
      toast.success('Review deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <section className="mb-12" data-testid="reviews-admin-section">
      {/* Dropdown header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 panel-luxe px-6 py-5 hover:border-gold/40 transition-colors"
        data-testid="reviews-admin-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 text-left">
          <ChatCircleText size={22} weight="duotone" className="text-gold" />
          <div>
            <h2 className="text-xl font-serif font-normal tracking-tight text-ivory">Customer Reviews</h2>
            <p className="text-xs text-ash font-sans mt-1">
              Manage testimonials shown on the welcome page.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {open && (
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash">
              {reviews.length} total
            </span>
          )}
          <CaretDown
            size={16}
            weight="bold"
            className={`text-ash transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-4 animate-fade-up" data-testid="reviews-admin-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm text-bone font-sans">
              Published reviews appear in the "What writers are saying" section on <code className="text-gold">/welcome</code>.
            </p>
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-gold text-charcoal hover:bg-gold-dark inline-flex items-center gap-2"
              data-testid="add-review-toggle"
            >
              <Plus size={14} weight="bold" />
              {showForm ? 'Close form' : 'Add review'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleCreate} className="panel-luxe p-6 space-y-4" data-testid="review-create-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Reviewer name *</label>
                  <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sarah J." required data-testid="review-name-input" />
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Location</label>
                  <input className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. London, UK" data-testid="review-location-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Project type</label>
                  <select className={inputCls} value={projectType} onChange={(e) => setProjectType(e.target.value)} data-testid="review-project-select">
                    {PROJECT_TYPES.map((p) => (<option key={p} value={p}>{p}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Rating *</label>
                  <div className="px-4 py-3 rounded-lg border border-border bg-background flex items-center gap-3">
                    <StarRow rating={rating} onChange={setRating} size={20} />
                    <span className="text-xs text-ash">{rating}/5</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash block mb-2">Review text *</label>
                <textarea
                  className={`${inputCls} min-h-[120px] resize-y`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Their words about the experience…"
                  required
                  data-testid="review-text-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-bone font-sans cursor-pointer">
                <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-gold" data-testid="review-published-toggle" />
                <span>Publish immediately (visible on welcome page)</span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-transparent text-ash border border-border hover:border-gold hover:text-gold">Cancel</button>
                <button type="submit" disabled={submitting} className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-5 py-2 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-50" data-testid="review-submit-btn">
                  {submitting ? 'Saving…' : 'Save review'}
                </button>
              </div>
            </form>
          )}

          {/* List */}
          {loading ? (
            <div className="panel-luxe p-6 text-center text-ash text-sm">Loading reviews…</div>
          ) : reviews.length === 0 ? (
            <div className="panel-luxe p-8 text-center" data-testid="reviews-empty">
              <ChatCircleText size={28} weight="duotone" className="text-gold mx-auto mb-3" />
              <p className="text-bone font-sans text-sm">No reviews yet. Add your first one above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.review_id} className={`panel-luxe p-5 flex items-start gap-4 ${!r.published ? 'opacity-60' : ''}`} data-testid={`review-row-${r.review_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1.5">
                      <span className="font-serif text-ivory text-base">{r.name}</span>
                      <StarRow rating={r.rating} readOnly size={12} />
                      {r.project_type && (
                        <span className="text-[10px] uppercase tracking-[0.28em] text-gold font-medium">{r.project_type}</span>
                      )}
                      {!r.published && (
                        <span className="text-[10px] uppercase tracking-[0.28em] text-rose-400 font-medium">Unpublished</span>
                      )}
                    </div>
                    {r.location && <p className="text-xs text-ash mb-2">{r.location}</p>}
                    <p className="text-sm text-bone leading-relaxed font-sans">{r.text}</p>
                  </div>
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <button
                      onClick={() => togglePublished(r)}
                      className="p-2 rounded-md hover:bg-stone text-ash hover:text-gold transition-colors"
                      title={r.published ? 'Unpublish' : 'Publish'}
                      data-testid={`review-toggle-${r.review_id}`}
                    >
                      {r.published ? <Eye size={16} weight="duotone" /> : <EyeSlash size={16} weight="duotone" />}
                    </button>
                    <button
                      onClick={() => handleDelete(r)}
                      className="p-2 rounded-md hover:bg-stone text-ash hover:text-rose-400 transition-colors"
                      title="Delete"
                      data-testid={`review-delete-${r.review_id}`}
                    >
                      <Trash size={16} weight="duotone" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
